import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import LoadingSpinner from "../LoadingSpinner";
import { bankContains, branchByBankAndContains } from "../../api/bankApi";
import { currencyContains } from "../../api/currencyApi";
import SearchDropdown from "../input/SearchDropdown";
import { getOtherRefSearchDropdown } from "../../api/keyvalue";
import Dropdown from "../input/Dropdown";
import Input from "../input/Input";
import {
  dynamicContactValidationStatus,
  dynamicFinanceInfoValidationState,
  financeInfoValidation,
} from "../validations/stakeholderCreation";
import { useSelector } from "react-redux";
import { emptyValidator } from "../../validators";
import { FORMS, MODULES } from "../../const";
import useFormVisibility from "../../hooks/useFormVisibility";
import useDynamicValidation from "../../hooks/useDynamicValidation";

const FORM = FORMS.STAKEHOLDER.FINANCE_INFO.form;

const StakeholderFinanceInfo = forwardRef(
  (
    {
      errors,
      dropdown,
      isLoading,
      currentValues,
      editMode,
      useDynamicFields = false,
      keyValue,
      type,
    },
    ref,
  ) => {
    const [financeInfo, setFinanceInfo] = useState(
      editMode && currentValues.length !== 0
        ? {
            ...currentValues[0],
            finCurrency: currentValues[0].finCurrencyId,
            finStakeholder: currentValues[0].finStakeholderId,
            finBillingCustomer: currentValues[0].finBillingCustomerId,
            finInvoicingCustomer: currentValues[0].finInvoicingCustomerId,
          }
        : {
            finCashType: 2,
            finCreditFrom: null,
            finCreditTo: null,
            finRemainingCredit: "",
            finRemainingOutstanding: "",
            finTaxType: null,
            finTaxRegistration: "",
            finCompanyRegistration: "",
            finSuspendedTaxNo: "",
            finSvatType: null,
            finCurrency: 143,
            finBank: null,
            finBranch: null,
            finAccNo: "",
            finAccBeneficiary: "",
            finBillingCustomer: null,
            finInvoicingCustomer: null,
            finStatus: 2,
            finActive: 1,
            finCreditAmount: "",
          },
    );

    const [bankSelected, setBankSelected] = useState(false);
    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [currentErrors, setCurrentErrors] = useState(errors);

    const isFormVisibilityLoading = useSelector(
      (state) => state.formVisibility.loading,
    );

    const [initialActive, setInitialActive] = useState(
          editMode ? currentValues?.finActive : 1,
        );

    const visMap = useSelector(
      (state) => state.formVisibility.formVisibilityMap,
    );

    useFormVisibility({
      form: FORM,
      keyValue,
      type,
      useDynamicFields,
    });

    const validationStatus = useDynamicValidation(
      dynamicFinanceInfoValidationState,
      FORM,
      useDynamicFields,
    );

    useEffect(() => {
      setCurrentErrors(errors);
    }, [errors]);

    const bankBranchRef = useRef();

    useImperativeHandle(ref, () => ({
      getData: () => ({
        ...financeInfo,
      }),
       isDeletedActive: () =>
        financeInfo.finActive === 2 && initialActive !== 2,
      resetForm: () => {
        setFinanceInfo({
          finCashType: 2,
          finCreditFrom: null,
          finCreditTo: null,
          finRemainingCredit: "",
          finRemainingOutstanding: "",
          finTaxType: null,
          finTaxRegistration: "",
          finCompanyRegistration: "",
          finSuspendedTaxNo: "",
          finSvatType: null,
          finCurrency: 143,
          finBank: null,
          finBranch: null,
          finAccNo: "",
          finAccBeneficiary: "",
          finBillingCustomer: null,
          finInvoicingCustomer: null,
          finStatus: 2,
          finActive: 1,
          finCreditAmount: "",
        });
        setSearchFieldReset(true);
      },
      validateForm: () => {
        return financeInfoValidation(financeInfo, validationStatus);
      },
    }));

    return (
      <>
        {isLoading || isFormVisibilityLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <div className="ui dividing header">Finance Info</div>

              <div className="two fields">
                <Dropdown
                  useDynamicFields={useDynamicFields}
                  label="Cash Type"
                  error={currentErrors.finCreditFrom}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_credit_from"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finCreditFrom: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Cash Type"
                  options={dropdown.cashTypeOptions}
                  style={{ height: "20px" }}
                  value={financeInfo.finCashType}
                  onChange={(e, data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finCashType: data.value,
                    }));
                  }}
                />
                <Dropdown
                  useDynamicFields={useDynamicFields}
                  label="Credit From"
                  error={currentErrors.finCreditFrom}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_credit_from"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finCreditFrom: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Credit From"
                  options={dropdown.creditFromOptions}
                  style={{ height: "20px" }}
                  value={financeInfo.finCreditFrom}
                  onChange={(e, data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finCreditFrom: data.value,
                    }));
                  }}
                />
              </div>

              <div className="two fields">
                <Input
                  useDynamicFields={useDynamicFields}
                  label="Remaining Outstanding Amount"
                  error={currentErrors.finRemainingOutstanding}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_remaining_outstanding"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finRemainingOutstanding: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Remaining Outstanding Amount"
                  value={financeInfo.finRemainingOutstanding}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finRemainingOutstanding: e.target.value,
                    }));
                  }}
                />
                <Dropdown
                  useDynamicFields={useDynamicFields}
                  label="Credit To"
                  error={currentErrors.finCreditTo}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_credit_to"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finCreditTo: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Credit To"
                  options={dropdown.creditToOptions}
                  style={{ height: "20px" }}
                  value={financeInfo.finCreditTo}
                  onChange={(e, data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finCreditTo: data.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <Input
                  useDynamicFields={useDynamicFields}
                  label="Remaining Credit Amount"
                  error={currentErrors.finRemainingCredit}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_remaining_credit"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finRemainingCredit: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="remainingCredit"
                  placeholder="Remaining Credit Amount"
                  value={financeInfo.finRemainingCredit}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finRemainingCredit: e.target.value,
                    }));
                  }}
                />
                <Input
                  useDynamicFields={useDynamicFields}
                  label="Credit Amount"
                  error={currentErrors.finCreditAmount}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_credit_amount"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finCreditAmount: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="creditAmount"
                  placeholder="Credit Amount"
                  value={financeInfo.finCreditAmount}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finCreditAmount: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <Dropdown
                  useDynamicFields={useDynamicFields}
                  label="Tax Type"
                  error={currentErrors.finTaxType}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_tax_type"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finTaxType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Tax Type"
                  options={dropdown.taxTypeOptions}
                  style={{ height: "20px" }}
                  value={financeInfo.finTaxType}
                  onChange={(e, data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finTaxType: data.value,
                    }));
                  }}
                />
                <Input
                  useDynamicFields={useDynamicFields}
                  label="Tax No"
                  error={currentErrors.finTaxRegistration}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_tax_registration"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finTaxRegistration: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="taxNo"
                  placeholder="Tax No"
                  value={financeInfo.finTaxRegistration}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finTaxRegistration: e.target.value,
                    }));
                  }}
                />
              </div>

              <div className="two fields">
                <Dropdown
                  useDynamicFields={useDynamicFields}
                  label="Svat Type"
                  error={currentErrors.finSvatType}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_svat_type"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { cluStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Svat Type"
                  options={dropdown.swatTypeOptions}
                  style={{ height: "20px" }}
                  value={financeInfo.finSvatType}
                  onChange={(e, data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finSvatType: data.value,
                    }));
                  }}
                />
                <Input
                  useDynamicFields={useDynamicFields}
                  label="SVAT No"
                  error={currentErrors.finSuspendedTaxNo}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_suspended_taxno"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finSuspendedTaxNo: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="suspendedTaxNo"
                  placeholder="SVAT No"
                  value={financeInfo.finSuspendedTaxNo}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finSuspendedTaxNo: e.target.value,
                    }));
                  }}
                />
              </div>

              <div className="two fields">
                <SearchDropdown
                  useDynamicFields={useDynamicFields}
                  key={1}
                  defaultValue={"Sri Lanka Rupee"}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_currency"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  onClear={() => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finCurrency: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finCurrency: data,
                    }))
                  }
                  api={currencyContains}
                  fieldInfo={{
                    tableName: "masterdata.tms_vm_reg_currency",
                    primaryKey: "cur_id",
                    fieldName: "cur_currency",
                    id: financeInfo.finCurrency,
                  }}
                  label="Currency"
                  error={currentErrors.finCurrency}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finCurrency: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
                <Input
                  useDynamicFields={useDynamicFields}
                  label="Company Registration No"
                  error={currentErrors.finCompanyRegistration}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_company_registration"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finCompanyRegistration: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="companyRegistration"
                  placeholder="Company Registration No"
                  value={financeInfo.finCompanyRegistration}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finCompanyRegistration: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <SearchDropdown
                  useDynamicFields={useDynamicFields}
                  key={2}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_bank"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finBank: -1,
                      finBranch: -1,
                    }));
                    setBankSelected(false);
                    bankBranchRef?.current?.reset();
                  }}
                  onResultSelect={(data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finBank: data,
                      finBranch: null,
                    }));

                    setBankSelected(true);
                    bankBranchRef?.current?.reset();
                  }}
                  api={bankContains}
                  fieldInfo={{
                    tableName: "masterdata.tms_vm_banks",
                    primaryKey: "bnk_id",
                    fieldName: "bnk_name",
                    id: financeInfo.finBank,
                  }}
                  label="Bank"
                  error={currentErrors.finBank}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finBank: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />

                <SearchDropdown
                  useDynamicFields={useDynamicFields}
                  key={3}
                  ref={bankBranchRef}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_branch"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finBranch: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finBranch: data,
                    }))
                  }
                  api={branchByBankAndContains}
                  apiDependency={{ bank: financeInfo.finBank }}
                  fieldInfo={{
                    tableName: "masterdata.tms_vm_bank_branches",
                    primaryKey: "brn_id",
                    fieldName: "brn_name",
                    id: financeInfo.finBranch,
                  }}
                  disabled={!bankSelected}
                  label="Branch"
                  error={currentErrors.finBranch}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finBranch: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
              </div>
              <div className="two fields">
                <Input
                  useDynamicFields={useDynamicFields}
                  label="Account No"
                  error={currentErrors.finAccNo}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_acc_no"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finAccNo: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="AccNo"
                  placeholder="Account No"
                  value={financeInfo.finAccNo}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finAccNo: e.target.value,
                    }));
                  }}
                />
                <Input
                  useDynamicFields={useDynamicFields}
                  label="Account Beneficiary"
                  error={currentErrors.finAccBeneficiary}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_acc_beneficiary"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finAccBeneficiary: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="AccBeneficiary"
                  placeholder="Account Beneficiary"
                  value={financeInfo.finAccBeneficiary}
                  onChange={(e) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finAccBeneficiary: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <SearchDropdown
                  useDynamicFields={useDynamicFields}
                  key={1}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_billing_customer"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finBillingCustomer: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finBillingCustomer: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Billing Customer",
                  }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: financeInfo.finBillingCustomer,
                  }}
                  label="Billing Customer"
                  error={currentErrors.finBillingCustomer}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finBillingCustomer: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />

                <SearchDropdown
                  useDynamicFields={useDynamicFields}
                  key={1}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_invoicing_customer"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finInvoicingCustomer: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finInvoicingCustomer: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Invoicee Customer",
                  }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: financeInfo.finInvoicingCustomer,
                  }}
                  label="Invoicing Customer"
                  error={currentErrors.finInvoicingCustomer}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finInvoicingCustomer: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
              </div>
              <div className="two fields">
                <Dropdown
                  useDynamicFields={useDynamicFields}
                  label="Status"
                  error={currentErrors.finStatus}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_status"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Status"
                  options={dropdown.statusOptions}
                  style={{ height: "20px" }}
                  value={financeInfo.finStatus}
                  onChange={(e, data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finStatus: data.value,
                    }));
                  }}
                />
                <Dropdown
                  useDynamicFields={useDynamicFields}
                  label="Active"
                  error={currentErrors.finActive}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="fin_active"
                  needDeletePermission={true}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { finActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Active"
                  options={dropdown.activeOptions}
                  style={{ height: "20px" }}
                  value={financeInfo.finActive}
                  onChange={(e, data) => {
                    setFinanceInfo((prevState) => ({
                      ...prevState,
                      finActive: data.value,
                    }));
                  }}
                />
              </div>
            </div>
          </form>
        )}
      </>
    );
  },
);

export default StakeholderFinanceInfo;
